/**
 * xeroQueueService — frontend helpers for the xero_call_queue + dispatcher.
 *
 * Every outbound Xero call gets enqueued (insert row → dispatcher picks up).
 * Callers can either fire-and-forget OR subscribe to the queue row via Realtime
 * to know when the dispatcher finishes.
 *
 * Design:
 *   - enqueue() inserts a row, optionally fires the dispatcher immediately for
 *     low-latency on user-initiated work, returns the queue row id.
 *   - awaitResult() subscribes to row changes; resolves on completed/failed.
 *   - Cron runs the dispatcher every minute as a safety net.
 *
 * Per-tenant + app-wide rate limits are enforced by the dispatcher:
 *   - 50 calls/min/tenant
 *   - 4,500 calls/day/tenant (Xero allows 5,000)
 *   - 4 concurrent in-flight per tenant
 *   - 9,000/day across all tenants (Xero allows 10,000 app-wide)
 */

import { supabase } from '@/integrations/supabase/client';

const DISPATCHER_URL =
  (import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/$/, '') +
  '/functions/v1/xero-queue-dispatcher';

export type XeroQueueIntent =
  | 'push_invoice'
  | 'refresh_invoice'
  | 'pull_invoices_delta'
  | 'arbitrary';

export type XeroQueueStatus =
  | 'pending'
  | 'in_flight'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface XeroQueueRow {
  id: string;
  integration_id: string;
  intent: XeroQueueIntent;
  priority: number;
  payload: Record<string, unknown>;
  related_invoice_id: string | null;
  status: XeroQueueStatus;
  attempts: number;
  max_attempts: number;
  next_attempt_at: string;
  result: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface EnqueueOptions {
  /** 0 = user-initiated (highest), 5 = automated, 9 = background */
  priority?: number;
  /** When true, also POST the dispatcher immediately for low-latency */
  triggerImmediate?: boolean;
  /** When true, the dispatcher emails the invoice to the contact after creating
   *  it in Xero (only takes effect for AUTHORISED invoices, i.e. status 'sent'). */
  send?: boolean;
}

const DEFAULT_PRIORITY = 5;

async function getActiveIntegrationId(organizationId: string): Promise<string | null> {
  const { data } = await supabase
    .from('xero_integrations')
    .select('id')
    .eq('organization_id', organizationId)
    .eq('connection_status', 'active')
    .maybeSingle();
  return (data?.id as string | undefined) ?? null;
}

async function triggerDispatcher(): Promise<void> {
  // Fire-and-forget POST to wake the dispatcher. We don't care about the response.
  // Auth: rely on the user's JWT (the function has verify_jwt=false but we pass
  // the session token anyway in case that gets tightened later).
  try {
    const { data: { session } } = await supabase.auth.getSession();
    void fetch(DISPATCHER_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({}),
    });
  } catch {
    // Cron-tick will pick it up within 60s if this fails
  }
}

/**
 * Enqueue a push_invoice intent. The dispatcher will execute the full
 * orchestration (idempotency lookup → contact upsert → invoice create) within
 * the per-tenant rate budget.
 */
export async function enqueueInvoicePush(args: {
  invoiceId: string;
  organizationId: string;
  options?: EnqueueOptions;
}): Promise<XeroQueueRow> {
  const integrationId = await getActiveIntegrationId(args.organizationId);
  if (!integrationId) {
    throw new Error('No active Xero integration for this organization');
  }

  const { data, error } = await supabase
    .from('xero_call_queue')
    .insert({
      integration_id: integrationId,
      intent: 'push_invoice' as XeroQueueIntent,
      priority: args.options?.priority ?? DEFAULT_PRIORITY,
      payload: args.options?.send ? { send: true } : {},
      related_invoice_id: args.invoiceId,
    })
    .select('*')
    .single();

  if (error) throw error;

  if (args.options?.triggerImmediate !== false) {
    void triggerDispatcher();
  }

  return data as XeroQueueRow;
}

/**
 * Bulk-enqueue many push_invoice intents in a single insert. Returns the
 * created queue rows. Always triggers the dispatcher once at the end.
 */
export async function enqueueInvoicePushes(args: {
  invoiceIds: string[];
  organizationId: string;
  options?: EnqueueOptions;
}): Promise<XeroQueueRow[]> {
  if (args.invoiceIds.length === 0) return [];

  const integrationId = await getActiveIntegrationId(args.organizationId);
  if (!integrationId) {
    throw new Error('No active Xero integration for this organization');
  }

  const rows = args.invoiceIds.map((invoiceId) => ({
    integration_id: integrationId,
    intent: 'push_invoice' as XeroQueueIntent,
    priority: args.options?.priority ?? DEFAULT_PRIORITY,
    payload: {},
    related_invoice_id: invoiceId,
  }));

  const { data, error } = await supabase
    .from('xero_call_queue')
    .insert(rows)
    .select('*');

  if (error) throw error;

  if (args.options?.triggerImmediate !== false) {
    void triggerDispatcher();
  }

  return (data ?? []) as XeroQueueRow[];
}

/**
 * Wait for a queue row to reach a terminal state (completed | failed |
 * cancelled). Resolves with the final row, or rejects on timeout.
 */
export function awaitQueueResult(
  queueId: string,
  timeoutMs: number = 5 * 60 * 1000,
): Promise<XeroQueueRow> {
  // Polls only. The Realtime subscription that used to run alongside was
  // removed on 2026-10-19 to cut database load; this poll was already the
  // path that resolved most pushes.
  return new Promise((resolve, reject) => {
    let done = false;

    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      clearInterval(poll);
      reject(new Error(`Queue row ${queueId} did not resolve within ${timeoutMs}ms`));
    }, timeoutMs);

    const isFinal = (row: XeroQueueRow) =>
      row.status === 'completed' || row.status === 'failed' || row.status === 'cancelled';
    const check = async () => {
      if (done) return;
      const { data } = await supabase
        .from('xero_call_queue')
        .select('*')
        .eq('id', queueId)
        .maybeSingle();
      if (!data || done) return;
      const row = data as XeroQueueRow;
      if (isFinal(row)) {
        done = true;
        clearTimeout(timer);
        clearInterval(poll);
        resolve(row);
      }
    };
    const poll = setInterval(() => void check(), 4000);
    void check();
  });
}

/**
 * Watch the organization's recent queue rows. Returns an unsubscribe
 * function. Used by the global progress toast.
 *
 * Fetches once, then polls every 4 s only while a row is pending or in
 * flight, and again when the tab comes back into view. When the queue is
 * idle it sends nothing. (This replaced a Realtime subscription on
 * 2026-10-19 to cut database load.) Re-subscribe to pick up a push that
 * was just started in this tab.
 */
export function subscribeToOrgQueue(
  organizationId: string,
  onChange: (rows: XeroQueueRow[]) => void,
): () => void {
  let integrationId: string | null = null;
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function refetch() {
    if (!integrationId || cancelled) return;
    if (timer) clearTimeout(timer);
    timer = null;
    const { data } = await supabase
      .from('xero_call_queue')
      .select('*')
      .eq('integration_id', integrationId)
      .in('status', ['pending', 'in_flight', 'completed', 'failed'])
      .gte('created_at', new Date(Date.now() - 30 * 60 * 1000).toISOString())
      .order('created_at', { ascending: false });
    if (cancelled) return;
    const rows = (data ?? []) as XeroQueueRow[];
    onChange(rows);
    if (rows.some((r) => r.status === 'pending' || r.status === 'in_flight')) {
      timer = setTimeout(() => void refetch(), 4000);
    }
  }

  const onVisible = () => {
    if (document.visibilityState === 'visible') void refetch();
  };
  document.addEventListener('visibilitychange', onVisible);

  void (async () => {
    integrationId = await getActiveIntegrationId(organizationId);
    await refetch();
  })();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}

/**
 * Cancel a pending queue row. No-op if it's already in_flight or terminal.
 */
export async function cancelQueueRow(queueId: string): Promise<void> {
  const { error } = await supabase
    .from('xero_call_queue')
    .update({ status: 'cancelled', completed_at: new Date().toISOString() })
    .eq('id', queueId)
    .eq('status', 'pending');
  if (error) throw error;
}
